/* Once Upon Magic — NEO 2026 — privacy-safe discovery core */
(() => {
  'use strict';

  const ROOT = window.Shopify?.routes?.root || '/';
  const POOL_SECTION_ID = 'oum-neo-swipe-pool';
  const MEMORY = { insights: { counts: {}, recent: [], firstSeen: Date.now() }, taste: { tokens: {}, updatedAt: 0 } };
  const KEYS = { insights: 'oum_insights_v2', taste: 'oum_taste_profile_v2' };
  let analyticsAllowed = false;
  let preferencesAllowed = false;
  let privacyResolved = false;

  const safeJSON = (value, fallback) => { try { return JSON.parse(value); } catch (_) { return fallback; } };
  const readLocal = (key, fallback) => { try { return safeJSON(localStorage.getItem(key) || '', fallback); } catch (_) { return fallback; } };
  const writeLocal = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {} };
  const removeLocal = (key) => { try { localStorage.removeItem(key); } catch (_) {} };
  const normalize = (value = '') => String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[’']/g, ' ').replace(/[^a-z0-9]+/g, ' ').trim();
  const tokens = (value = '') => normalize(value).split(' ').filter((token) => token.length > 2);
  const unique = (values) => [...new Set(values.filter(Boolean))];

  const hydrateAllowedState = () => {
    if (analyticsAllowed) Object.assign(MEMORY.insights, readLocal(KEYS.insights, MEMORY.insights));
    if (preferencesAllowed) Object.assign(MEMORY.taste, readLocal(KEYS.taste, MEMORY.taste));
  };

  const refreshPrivacy = () => {
    const api = window.Shopify?.customerPrivacy;
    if (!api) return;
    try { analyticsAllowed = api.analyticsProcessingAllowed() === true; } catch (_) { analyticsAllowed = false; }
    try { preferencesAllowed = api.preferencesProcessingAllowed() === true; } catch (_) { preferencesAllowed = false; }
    privacyResolved = true;
    hydrateAllowedState();
    window.dispatchEvent(new CustomEvent('oum:privacy-change', { detail: { analyticsAllowed, preferencesAllowed } }));
  };

  const ready = new Promise((resolve) => {
    const finish = () => { refreshPrivacy(); resolve(); };
    if (typeof window.Shopify?.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => finish());
    } else finish();
  });
  document.addEventListener('visitorConsentCollected', refreshPrivacy);

  window.OUMPrivacy = {
    ready,
    get analyticsAllowed() { return analyticsAllowed; },
    get preferencesAllowed() { return preferencesAllowed; },
    get resolved() { return privacyResolved; },
    refresh: refreshPrivacy
  };
  window.OUMStorage = { readLocal, writeLocal, removeLocal };

  const track = (name, detail = {}) => {
    if (!name || !analyticsAllowed) return;
    MEMORY.insights.counts[name] = (MEMORY.insights.counts[name] || 0) + 1;
    MEMORY.insights.recent.push({ name, at: Date.now(), detail });
    MEMORY.insights.recent = MEMORY.insights.recent.slice(-120);
    writeLocal(KEYS.insights, MEMORY.insights);
    const payload = { event: `oum_${name}`, ...detail };
    if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
    window.dispatchEvent(new CustomEvent('oum:insight', { detail: { name, ...detail } }));
    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish === 'function') {
      try { const result = publish.call(window.Shopify.analytics, `oum_${name}`, detail); result?.catch?.(() => {}); } catch (_) {}
    }
  };
  window.OUMInsights = {
    track,
    getSummary: () => ({ ...MEMORY.insights, counts: { ...MEMORY.insights.counts }, recent: [...MEMORY.insights.recent] }),
    export: () => JSON.stringify(MEMORY.insights, null, 2),
    clear: () => { MEMORY.insights = { counts: {}, recent: [], firstSeen: Date.now() }; removeLocal(KEYS.insights); }
  };

  const productTokens = (product = {}) => unique([
    ...tokens(product.title), ...tokens(product.tags), ...tokens(product.productType), ...tokens(product.vendor),
    ...String(product.collections || '').split('|').flatMap(tokens)
  ]).filter((token) => !['disneyland', 'paris', 'disney', 'produit', 'nouveaute', 'boutique'].includes(token));
  const rewardProduct = (product, points = 1) => {
    if (!preferencesAllowed) return;
    productTokens(product).forEach((token) => { MEMORY.taste.tokens[token] = Math.max(-10, Math.min(30, (MEMORY.taste.tokens[token] || 0) + points)); });
    MEMORY.taste.updatedAt = Date.now(); writeLocal(KEYS.taste, MEMORY.taste);
  };
  const tasteScore = (product) => preferencesAllowed ? productTokens(product).reduce((sum, token) => sum + (MEMORY.taste.tokens[token] || 0), 0) : 0;
  window.OUMTaste = { rewardProduct, score: tasteScore, profile: () => ({ ...MEMORY.taste, tokens: { ...MEMORY.taste.tokens } }) };

  const preservePreview = (url) => {
    const params = new URLSearchParams(location.search);
    const previewId = params.get('preview_theme_id') || (window.Shopify?.designMode ? window.Shopify?.theme?.id : '');
    if (previewId) url.searchParams.set('preview_theme_id', String(previewId));
  };
  const buildSectionUrl = (feedUrl, page = 1, sectionId = POOL_SECTION_ID) => {
    const url = new URL(feedUrl || `${ROOT}collections/all`, location.origin);
    if (ROOT !== '/' && !url.pathname.startsWith(ROOT)) url.pathname = `${ROOT.replace(/\/$/, '')}${url.pathname}`;
    url.searchParams.set('page', String(page)); url.searchParams.set('section_id', sectionId); preservePreview(url); return url.toString();
  };
  const parsePoolItem = (el) => ({
    id: String(el.dataset.productId || ''), handle: el.dataset.handle || '', url: el.dataset.url || '#', title: el.dataset.title || '',
    price: el.dataset.price || '', priceCents: Number(el.dataset.priceCents || 0), image: el.dataset.image || '', imageAlt: el.dataset.imageAlt || el.dataset.title || '',
    sourceLabel: el.dataset.sourceLabel || '', productType: el.dataset.productType || '', vendor: el.dataset.vendor || '', tags: el.dataset.tags || '',
    collections: el.dataset.collections || '', availability: el.dataset.availability || 'stock', availabilityLabel: el.dataset.availabilityLabel || '',
    variantId: el.dataset.variantId || '', inventory: Number(el.dataset.inventory || 0), publishedAt: el.dataset.publishedAt || ''
  });
  const fetchPage = async (feedUrl, page = 1) => {
    const response = await fetch(buildSectionUrl(feedUrl, page), { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error(`Catalogue ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html'); const pool = doc.querySelector('[data-oum-swipe-pool]');
    if (!pool) throw new Error('Catalogue pool missing');
    return { pages: Math.max(1, Number(pool.dataset.pages || 1)), total: Number(pool.dataset.total || 0), items: [...pool.querySelectorAll('[data-oum-swipe-pool-item]')].map(parsePoolItem) };
  };
  window.OUMCatalog = { fetchPage, buildSectionUrl, parsePoolItem, preservePreview, normalize, unique };

  const aliases = new Map(Object.entries({
    stich:'stitch','stich disney':'stitch disney',loungfly:'loungefly','lounge fly':'loungefly','mini backpack':'loungefly','mini sac a dos':'loungefly',
    rapunzel:'raiponce',raiponse:'raiponce',tangled:'raiponce',frozen:'reine des neiges','beauty and the beast':'belle et la bete','little mermaid':'ariel',
    'cheshire cat':'chat du cheshire','snow white':'blanche neige','sleeping beauty':'aurore','lion king':'roi lion','the lion king':'roi lion',
    'nightmare before christmas':'etrange noel monsieur jack',lorcanna:'lorcana','disney land paris':'disneyland paris',micky:'mickey',minie:'minnie'
  }));
  let rewriting = false;
  const corrected = (value) => aliases.get(normalize(value));
  document.addEventListener('input', (event) => {
    const input = event.target; if (rewriting || !(input instanceof HTMLInputElement) || (input.type !== 'search' && input.name !== 'q')) return;
    const replacement = corrected(input.value); if (!replacement || normalize(input.value) === normalize(replacement)) return;
    rewriting = true; const previous = input.value; input.value = replacement; track('search_alias', { from: normalize(previous), to: replacement });
    input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertReplacementText', data: replacement })); rewriting = false;
  }, true);
  document.addEventListener('submit', (event) => {
    const input = event.target instanceof HTMLFormElement ? event.target.querySelector('input[type="search"],input[name="q"]') : null;
    if (input instanceof HTMLInputElement) { const replacement = corrected(input.value); if (replacement) input.value = replacement; }
  }, true);
})();
